/**
 * UpdateBanner
 * 
 * Banner fixo que aparece quando há uma nova versão disponível.
 * Fica visível até o usuário clicar para atualizar ou dispensar a build atual.
 */
import { useEffect, useState } from 'react';
import { RefreshCw, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useVersionCheck } from '@/hooks/useVersionCheck';

const DISMISS_BUILD_KEY = 'update_banner_dismissed_build_id';

export function UpdateBanner() {
  const {
    updateAvailable,
    serverVersion,
    isUpdating,
    updateStep,
    performUpdate,
  } = useVersionCheck();

  const [dismissed, setDismissed] = useState(false);
  const serverBuildId = serverVersion?.buildId;

  // Persistir dispensa apenas da build atual para não esconder atualizações futuras
  useEffect(() => {
    if (!updateAvailable || !serverBuildId) {
      setDismissed(false);
      return;
    }

    try {
      const dismissedBuildId = localStorage.getItem(DISMISS_BUILD_KEY);
      setDismissed(dismissedBuildId === serverBuildId);
    } catch {
      setDismissed(false);
    }
  }, [updateAvailable, serverBuildId]);

  if (!updateAvailable || dismissed) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-[100] max-w-md mx-auto animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-primary text-primary-foreground rounded-lg shadow-lg p-4 flex items-center gap-3">
        {isUpdating ? (
          <Loader2 className="w-5 h-5 flex-shrink-0 animate-spin" />
        ) : (
          <RefreshCw className="w-5 h-5 flex-shrink-0 animate-pulse" />
        )}
        
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">
            {isUpdating ? updateStep || 'Atualizando...' : 'Nova versão disponível'}
          </p>
          <p className="text-xs opacity-80 truncate">
            {isUpdating
              ? 'Aguarde, não feche o app'
              : `Versão ${serverVersion?.version || 'nova'} pronta`}
          </p>
        </div>
        
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            size="sm"
            variant="secondary"
            className="h-8 px-3 text-xs"
            onClick={performUpdate}
            disabled={isUpdating}
          >
            {isUpdating ? 'Aguarde...' : 'Atualizar'}
          </Button>
          
          {!isUpdating && (
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
              onClick={() => {
                setDismissed(true);
                if (!serverBuildId) return;
                try {
                  localStorage.setItem(DISMISS_BUILD_KEY, serverBuildId);
                } catch {
                  // ignore
                }
              }}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
